import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import { ApiService, HttpError, TApiFetchResponse } from 'web_core_library';
import { LanguageService } from '@web_opxp/language';
import StatsCache from './cache';
import * as Selectors from './selectors';
import { StatsActions } from './slice';
import StatsService from './statsService';
import * as Types from './types';
import {
  convertIUserStats2IUserStatGets,
  convertIUserStats2IUserStatPosts,
  updateExistingStatsArrayWithAnother,
} from './utils';

export function* initStatsSaga(action: PayloadAction<number>) {
  const userId = action.payload;
  yield call(StatsService.init, ApiService);
  yield call(StatsCache.init, userId);
  yield call(loadStatsWithCacheSaga);
  yield put(StatsActions.statsReady());
}

export function* loadUserStatsSaga(action: PayloadAction<number | undefined>) {
  const userId = action.payload;
  if (!userId) {
    // current user - use cache
    yield call(loadStatsWithCacheSaga);
    return;
  }
  // if user is set - its a custom user and we should go without caching
  yield call(loadStatsWithoutCache, userId);
}

export function* loadStatsWithCacheSaga() {
  let stats: Types.IUserStatGet[] | undefined = undefined;
  const cacheValue: Types.IUserStatGetCache | undefined = yield call(StatsCache.getStats);
  const isExpired: boolean = yield call(StatsCache.isExpired, cacheValue);
  if (!cacheValue || isExpired) {
    // start loading data from api if cached data not present or expired
    stats = yield call(fetchStatsFromApi);
    if (stats) {
      yield call(updateCache, stats);
    }
  }
  if (!stats) {
    if (!cacheValue) {
      // Show error if no stats available from API nor cached data
      const t = LanguageService.getTFunction();
      throw new Types.StatsError(t('common:Errors.Stats.LoadStatsFailed'));
    }
    // update state from cache regardless of cache expiration
    stats = cacheValue.stats;
  }
  yield put(StatsActions.updateStats(stats));
  yield put(StatsActions.statsReady());
}

export function* loadStatsWithoutCache(userId?: number) {
  const stats: Types.IUserStatGet[] | undefined = yield call(fetchStatsFromApi, userId);
  if (!stats) {
    // Show error if no stats available from API nor cached data
    const t = LanguageService.getTFunction();
    throw new Types.StatsError(t('common:Errors.Stats.LoadStatsFailed'));
  }
  yield put(StatsActions.updateStats(stats, userId));
  yield put(StatsActions.statsReady());
}

export function* fetchStatsFromApi(userId?: number) {
  try {
    // fetch data from api
    const statsResponse: TApiFetchResponse<typeof StatsService.getUserStats> = yield call(
      StatsService.getUserStats,
      userId
    );
    return statsResponse.data.stats;
  } catch {
    return;
  }
}

export function* updateCache(newData: Types.IUserStatGet[]) {
  //save stats with current timestamp to cache
  try {
    yield call(StatsCache.setStats, newData);
  } catch {
    // failed saving to cache can be safely ignored
  }
}

export function* saveStatsQueueSaga(action: PayloadAction<number | undefined>) {
  const userId = action.payload;
  const existingQueue: Types.IUserStat[] = yield select(Selectors.getStatsQueueToSave, userId);
  // don't send empty data to backend
  if (!existingQueue || !existingQueue.length) {
    yield put(StatsActions.saveStatsQueueSuccess([]));
    return;
  }
  try {
    yield call(StatsService.saveUserStats, convertIUserStats2IUserStatPosts(existingQueue), userId);
    if (!userId) {
      // if userId not specified we need to cache default users values
      // get stats from cache, update saved and save back to cache
      const cacheValue: Types.IUserStatGetCache | undefined = yield call(StatsCache.getStats);
      if (cacheValue?.stats) {
        const changedStats = convertIUserStats2IUserStatGets(existingQueue);
        const stats = updateExistingStatsArrayWithAnother<Types.IUserStatGet>(cacheValue.stats, changedStats);
        yield call(updateCache, stats);
      }
    }
    yield put(StatsActions.saveStatsQueueSuccess(existingQueue, userId));
  } catch (error) {
    const { data, status } = error as HttpError;
    const message = data?.msg ? data.msg : (error as Error).message;
    // dispatch failed action
    yield put(StatsActions.saveStatsQueueFailed(message));
    // rethrow error to the global handler;
    const t = LanguageService.getTFunction();
    if (status === 0 || status > 500) {
      throw new Types.StatsError(t('common:Errors.Stats.NetworkError_SaveStatsFailed'));
    } else if (status !== 422) {
      // supress the case which must be covered by calling feature
      throw new Types.StatsError(t('common:Errors.Stats.ServerError_SaveStatsFailed'));
    }
  }
}

export function* addQueueSaga(action: PayloadAction<Types.IStatsPayload>) {
  const { stats, userId } = action.payload;
  const existingQueue: Types.IUserStat[] = yield select(Selectors.getStatsQueueToSave, userId);
  const updatedQueue = updateExistingStatsArrayWithAnother(existingQueue, stats);
  yield put(StatsActions.updateStatsQueue(updatedQueue, userId));

  const existingStats: Types.IUserStat[] = yield select(Selectors.getStats, userId);
  const updatedStats = updateExistingStatsArrayWithAnother(existingStats, stats);
  yield put(StatsActions.updateStats(updatedStats, userId));
}

export function* preloadAgeGroupSaga(userId?: number) {
  const ageGroupId: number | null = yield select(Selectors.getAgeGroup, userId);
  if (ageGroupId !== null) {
    // age group already loaded, no need to load again
    return;
  }
  yield put(StatsActions.loadPeerGroups(userId));
  yield take(StatsActions.updatePeerGroups);
}

export function* loadPeerGroupSaga(action: PayloadAction<number | undefined>) {
  const userId = action.payload;
  const peergroupsResponse: TApiFetchResponse<typeof StatsService.getUserPeerGroup> = yield call(
    StatsService.getUserPeerGroup,
    userId
  );
  const peergroups = peergroupsResponse.data.peergroups;
  yield put(StatsActions.updatePeerGroups(peergroups, userId));
}

export function* saveAgeGroup(action: PayloadAction<number>) {
  const ageGroup = action.payload;
  // save peer group
  yield call(StatsService.saveUserAgeGroup, ageGroup);
  // refresh data by fetching new peergroups
  yield put(StatsActions.loadPeerGroups());
}

export function* preloadStatsSaga() {
  const isStatsLoading: boolean = yield select(Selectors.isStatsLoading);
  if (isStatsLoading) {
    yield take(StatsActions.statsReady);
  }
}

export default function* statsWatcher() {
  yield all([
    takeEvery(StatsActions.initStats, initStatsSaga),
    takeEvery(StatsActions.loadStats, loadUserStatsSaga),
    takeEvery(StatsActions.loadPeerGroups, loadPeerGroupSaga),
    takeEvery(StatsActions.saveAgeGroup, saveAgeGroup),
    takeEvery(StatsActions.addStatsQueue, addQueueSaga),
    takeEvery(StatsActions.saveStatsQueue, saveStatsQueueSaga),
  ]);
}
